<template>
  <div class="image-display">
    <div v-if="loading" class="flex justify-center items-center p-4">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
    </div>
    
    <div v-else-if="error" class="text-red-600 p-4 text-center">
      {{ error }}
    </div>
    
    <div v-else-if="!hasImages" class="text-gray-500 p-4 text-center">
      Aucune image disponible
    </div>
    
    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <div 
        v-for="image in images" 
        :key="image.uuid" 
        class="relative group bg-gray-100 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
      >
        <!-- Image -->
        <img 
          :src="getImageUrl(image)" 
          :alt="image.original_filename || 'Image'"
          class="w-full h-48 object-cover cursor-pointer"
          loading="lazy"
          @click="openImageModal(image)"
        />
        
        <!-- Overlay avec actions -->
        <div class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
          <div class="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex space-x-2">
            <button
              v-if="canDelete"
              class="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full transition-colors"
              :disabled="deleting === image.uuid"
              title="Supprimer l'image"
              @click.stop="deleteImage(image.uuid)"
            >
              <TrashIcon v-if="deleting !== image.uuid" class="w-4 h-4" />
              <div v-else class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            </button>
            
            <button
              class="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full transition-colors"
              title="Télécharger l'image"
              @click.stop="downloadImage(image)"
            >
              <DownloadIcon class="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <!-- Nom du fichier -->
        <div class="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 text-sm truncate">
          {{ image.original_filename || 'Image' }}
        </div>
      </div>
    </div>
    
    <!-- Modal pour afficher l'image en grand -->
    <div 
      v-if="selectedImage" 
      class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
      @click="closeImageModal"
    >
      <div class="relative max-w-4xl max-h-full p-4">
        <button
          class="absolute top-4 right-4 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75 transition-colors z-10"
          @click="closeImageModal"
        >
          <XIcon class="w-6 h-6" />
        </button>
        
        <img 
          :src="getImageUrl(selectedImage)" 
          :alt="selectedImage.original_filename || 'Image'"
          class="max-w-full max-h-full object-contain"
          @click.stop
        />
        
        <div class="absolute bottom-4 left-4 right-4 bg-black bg-opacity-50 text-white p-3 rounded">
          <div class="text-sm">
            <div class="font-semibold">{{ selectedImage.original_filename || 'Image' }}</div>
            <div class="text-gray-300">
              {{ formatFileSize(selectedImage.file_size) }} • 
              {{ formatDate(selectedImage.created_at) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ImageAsset } from '@/api/types/core/image';
import { Download as DownloadIcon, Trash2 as TrashIcon, X as XIcon } from 'lucide-vue-next';
import { computed, ref } from 'vue';

interface Props {
  images: ImageAsset[];
  loading?: boolean;
  error?: string | null;
  canDelete?: boolean;
  getImageUrl: (image: ImageAsset) => string;
  onDelete?: (imageUuid: string) => Promise<void>;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  error: null,
  canDelete: false,
});

const selectedImage = ref<ImageAsset | null>(null);
const deleting = ref<string | null>(null);

const hasImages = computed(() => props.images.length > 0);

const openImageModal = (image: ImageAsset): void => {
  selectedImage.value = image;
};

const closeImageModal = (): void => {
  selectedImage.value = null;
};

const deleteImage = async (imageUuid: string): Promise<void> => {
  if (!props.onDelete) return;
  
  deleting.value = imageUuid;
  try {
    await props.onDelete(imageUuid);
  } finally {
    deleting.value = null;
  }
};

const downloadImage = (image: ImageAsset): void => {
  const link = document.createElement('a');
  link.href = props.getImageUrl(image);
  link.download = image.original_filename || 'image';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};
</script>

<style scoped>
.image-display {
  @apply w-full;
}
</style> 